/**
 * useCollabSocket — Real-time collaboration WebSocket hook.
 *
 * Manages:
 *  - Auth handshake (sends JWT as first message)
 *  - Presence (joined/left/idle/typing)
 *  - Cursor sharing
 *  - Yjs update relay
 *  - File operations broadcast
 *  - Auto-reconnect with exponential backoff
 *  - Heartbeat ping every 25s
 *
 * Usage:
 *   const collab = useCollabSocket(projectId, { enabled: true });
 *   collab.sendCursorMove(file, line, col);
 *   collab.sendYjsUpdate(file, updateBase64);
 *   collab.members  → [{user_id, email, color, status, current_file}]
 */
import { useEffect, useRef, useState, useCallback } from 'react';
import { getCollabWsUrl } from '../services/api';

const RECONNECT_MAX_DELAY = 30;
const HEARTBEAT_INTERVAL = 25_000;

function getToken() {
  return localStorage.getItem('auth_token');
}

export default function useCollabSocket(projectId, { enabled = true } = {}) {
  const [connected, setConnected] = useState(false);
  const [members, setMembers] = useState([]);       // all presence entries
  const [myColor, setMyColor] = useState(null);
  const [myRole, setMyRole] = useState(null);
  const [cursors, setCursors] = useState({});        // {user_id: {file,line,col,color}}
  const [fileOps, setFileOps] = useState(null);      // last file_op event
  const [aiDiff, setAiDiff] = useState(null);        // pending AI diff for review
  const [reconnectAttempt, setReconnectAttempt] = useState(0);

  // Callbacks registered by consumers
  const onYjsUpdateRef = useRef(null);   // (file, updateBase64) => void
  const onYjsStateRef = useRef(null);    // (file, stateBase64) => void
  const onAiAppliedRef = useRef(null);   // (files) => void

  const wsRef = useRef(null);
  const shouldConnectRef = useRef(false);
  const isMountedRef = useRef(true);
  const reconnectTimerRef = useRef(null);
  const heartbeatRef = useRef(null);
  const connectRef = useRef(null);

  const clearTimers = useCallback(() => {
    if (reconnectTimerRef.current) { clearTimeout(reconnectTimerRef.current); reconnectTimerRef.current = null; }
    if (heartbeatRef.current) { clearInterval(heartbeatRef.current); heartbeatRef.current = null; }
  }, []);

  const closeSocket = useCallback(() => {
    const ws = wsRef.current;
    if (!ws) return;
    try { ws.onopen = ws.onmessage = ws.onerror = ws.onclose = null; ws.close(1000); } catch {}
    wsRef.current = null;
  }, []);

  const send = useCallback((msg) => {
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify(msg));
    }
  }, []);

  const connect = useCallback(() => {
    if (!shouldConnectRef.current || !projectId) return;
    if (wsRef.current && (wsRef.current.readyState === WebSocket.OPEN || wsRef.current.readyState === WebSocket.CONNECTING)) return;

    clearTimers();
    const url = getCollabWsUrl(projectId);
    if (!url) return;

    const ws = new WebSocket(url);
    wsRef.current = ws;

    ws.onopen = () => {
      if (!isMountedRef.current) return;
      // Auth handshake — send JWT as first message
      const token = getToken();
      ws.send(JSON.stringify({ type: 'auth', token }));
    };

    ws.onmessage = (e) => {
      if (!isMountedRef.current) return;
      let msg;
      try { msg = JSON.parse(e.data); } catch { return; }

      switch (msg.type) {
        case 'room_joined':
          setConnected(true);
          setReconnectAttempt(0);
          setMyColor(msg.your_color);
          setMyRole(msg.your_role);
          setMembers(msg.members || []);
          // Start heartbeat
          heartbeatRef.current = setInterval(() => send({ type: 'ping' }), HEARTBEAT_INTERVAL);
          break;

        case 'presence_update':
          setMembers(prev => {
            const idx = prev.findIndex(m => m.user_id === msg.user_id);
            const updated = { ...msg };
            if (idx >= 0) {
              const next = [...prev];
              next[idx] = { ...next[idx], ...updated };
              return next;
            }
            if (msg.status === 'offline') return prev;
            return [...prev, updated];
          });
          // Remove from members list if offline
          if (msg.status === 'offline') {
            setMembers(prev => prev.filter(m => m.user_id !== msg.user_id));
            setCursors(prev => { const n = { ...prev }; delete n[msg.user_id]; return n; });
          }
          break;

        case 'cursor_update':
          setCursors(prev => ({
            ...prev,
            [msg.user_id]: { file: msg.file, line: msg.line, col: msg.col, selection: msg.selection, color: msg.color },
          }));
          break;

        case 'yjs_update':
          onYjsUpdateRef.current?.(msg.file, msg.update);
          break;

        case 'yjs_state':
          onYjsStateRef.current?.(msg.file, msg.state);
          break;

        case 'file_op':
          setFileOps(msg);
          break;

        case 'ai_diff_ready':
          setAiDiff(msg);
          break;

        case 'ai_applied':
          setAiDiff(null);
          onAiAppliedRef.current?.(msg.files);
          break;

        case 'error':
          console.warn('[collab] server error:', msg.message);
          break;

        case 'pong':
          break;

        default:
          break;
      }
    };

    ws.onerror = () => { if (isMountedRef.current) setConnected(false); };

    ws.onclose = (ev) => {
      if (!isMountedRef.current) return;
      if (wsRef.current !== ws) return;
      wsRef.current = null;
      clearTimers();
      setConnected(false);

      if (!shouldConnectRef.current || ev?.code === 1000 || ev?.code === 4001 || ev?.code === 4003) return;

      setReconnectAttempt(prev => {
        const next = prev + 1;
        const delay = Math.min(2 ** (next - 1), RECONNECT_MAX_DELAY) * 1000;
        reconnectTimerRef.current = setTimeout(() => connectRef.current?.(), delay);
        return next;
      });
    };
  }, [projectId, clearTimers, send]);

  connectRef.current = connect;

  useEffect(() => {
    isMountedRef.current = true;
    shouldConnectRef.current = enabled && !!projectId;

    if (!enabled || !projectId) {
      closeSocket();
      clearTimers();
      setConnected(false);
      setMembers([]);
      return;
    }

    connect();

    return () => {
      isMountedRef.current = false;
      shouldConnectRef.current = false;
      clearTimers();
      closeSocket();
    };
  }, [enabled, projectId, connect, clearTimers, closeSocket]);

  // ── Public send helpers ───────────────────────────────────────────────────

  const sendCursorMove = useCallback((file, line, col, selection = null) => {
    send({ type: 'cursor_move', file, line, col, selection });
  }, [send]);

  const sendFileOpen = useCallback((file) => {
    send({ type: 'file_open', file });
  }, [send]);

  const sendTypingStart = useCallback((file) => {
    send({ type: 'typing_start', file });
  }, [send]);

  const sendTypingStop = useCallback((file) => {
    send({ type: 'typing_stop', file });
  }, [send]);

  const sendYjsUpdate = useCallback((file, updateBase64) => {
    send({ type: 'yjs_update', file, update: updateBase64 });
  }, [send]);

  const requestYjsSync = useCallback((file) => {
    send({ type: 'yjs_sync_request', file });
  }, [send]);

  const sendFileOp = useCallback((op, path, newPath = null) => {
    send({ type: 'file_op', op, path, new_path: newPath });
  }, [send]);

  // ── Callback registration ─────────────────────────────────────────────────

  const onYjsUpdate = useCallback((fn) => { onYjsUpdateRef.current = fn; }, []);
  const onYjsState = useCallback((fn) => { onYjsStateRef.current = fn; }, []);
  const onAiApplied = useCallback((fn) => { onAiAppliedRef.current = fn; }, []);

  return {
    connected,
    members,
    myColor,
    myRole,
    cursors,
    fileOps,
    aiDiff,
    reconnectAttempt,
    sendCursorMove,
    sendFileOpen,
    sendTypingStart,
    sendTypingStop,
    sendYjsUpdate,
    requestYjsSync,
    sendFileOp,
    onYjsUpdate,
    onYjsState,
    onAiApplied,
    clearAiDiff: () => setAiDiff(null),
  };
}
